import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import * as yup from "yup";
import { useFormik } from "formik";

import LoadingButton from "../../Buttons/Loading";
import { createClientWithEmail } from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";
import PropertiesSearchDialog from "../PropertiesSearch";
import { setClient } from "../../../store/features/clients/slice";

const CreateClientStepper = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const [showPropertiesSearch, setShowPropertiesSearch] = useState(false);
  const [clientPropertyIds, setClientPropertyIds] = useState([]);
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const properties = useSelector((state) => state.properties.byId);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();
  const steps = ["Name", "Email", "Properties"];

  const nameForm = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
    },
    onSubmit: (values) => nameFormSubmit(values),
    validationSchema: yup.object({
      firstName: yup
        .string()
        .max(80, "Must be 80 characters or less.")
        .required("Required"),
      lastName: yup
        .string()
        .max(80, "Must be 80 characters or less.")
        .required("Required"),
    }),
  });

  const emailForm = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: (values) => emailFormSubmit(values),
    validationSchema: yup.object({
      email: yup.string().email("Must be a valid email.").required("Required"),
    }),
  });

  const emailFormSubmit = () => {
    handleNext();
  };

  const nameFormSubmit = () => {
    handleNext();
  };

  const createClient = async () => {
    if (clientPropertyIds.length === 0)
      return enqueueSnackbar("Add at least 1 property", {
        variant: "error",
      });
    setNextButtonLoading(true);
    const input = {
      email: emailForm.values.email,
      firstName: nameForm.values.firstName,
      lastName: nameForm.values.lastName,
      serviceProviderId,
      propertyIds: clientPropertyIds,
    };

    try {
      const client = await createClientWithEmail(input);
      setNextButtonLoading(false);
      dispatch(setClient({ client }));
      enqueueSnackbar("Client created", { variant: "success" });
      onClose();
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setNextButtonLoading(false);
    }
  };

  const getNextHandler = () => {
    switch (activeStep) {
      case 0:
        return nameForm.submitForm();
      case 1:
        return emailForm.submitForm();
      case 2:
        return createClient();
      default:
        return () => {};
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepButtons = () => {
    return (
      <div className={classes.actionsContainer}>
        <>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className={classes.button}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={getNextHandler}
            className={classes.button}
            isLoading={nextButtonLoading}
          >
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </LoadingButton>
        </>
      </div>
    );
  };

  const renderName = () => {
    const firstNameError =
      nameForm.touched.firstName && nameForm.errors.firstName;
    const lastNameError = nameForm.touched.lastName && nameForm.errors.lastName;
    return (
      <>
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={12} sm={6}>
            <TextField
              id={"firstName"}
              label="First Name"
              onBlur={nameForm.onBlur}
              value={nameForm.values.firstName}
              onChange={nameForm.handleChange}
              required
              variant="filled"
              fullWidth
              error={firstNameError}
              helperText={firstNameError ? nameForm.errors.firstName : null}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={"lastName"}
              label="Last Name"
              onBlur={nameForm.onBlur}
              value={nameForm.values.lastName}
              onChange={nameForm.handleChange}
              required={true}
              variant="filled"
              fullWidth
              error={lastNameError}
              helperText={lastNameError ? nameForm.errors.lastName : null}
            />
          </Grid>
        </Grid>
        {renderStepButtons()}
      </>
    );
  };

  const renderEmail = () => {
    const emailError = emailForm.touched.email && emailForm.errors.email;
    return (
      <>
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={12} sm={12}>
            <TextField
              id={"email"}
              label="Email"
              onBlur={emailForm.onBlur}
              value={emailForm.values.email}
              onChange={emailForm.handleChange}
              required
              variant="filled"
              fullWidth
              error={emailError}
              helperText={emailError ? emailForm.errors.email : null}
            />
          </Grid>
        </Grid>
        {renderStepButtons()}
      </>
    );
  };

  const renderProperties = () => {
    return (
      <>
        {clientPropertyIds.map((propertyId) => {
          return (
            <Chip
              className={classes.chip}
              key={propertyId}
              label={properties[propertyId].displayName}
              onDelete={() =>
                setClientPropertyIds((prevState) =>
                  prevState.filter((id) => id !== propertyId)
                )
              }
            />
          );
        })}
        <Button color="primary" onClick={() => setShowPropertiesSearch(true)}>
          Add
        </Button>
        {renderStepButtons()}
      </>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderName();
      case 1:
        return renderEmail();
      case 2:
        return renderProperties();
      default:
        return "Unknown step";
    }
  };

  const isAdmin = isUserRole({ admin: true });

  if (!isAdmin)
    return (
      <>
        <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
          <DialogTitle>Create client</DialogTitle>
          <DialogContent>
            <DialogContentText>You are not the correct role.</DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
        <DialogTitle>Create Client</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation={"vertical"}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>{getStepContent(index)}</StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <PropertiesSearchDialog
        onPropertyClick={(propertyId) =>
          setClientPropertyIds((prevState) => [...prevState, propertyId])
        }
        open={showPropertiesSearch}
        onClose={() => setShowPropertiesSearch(false)}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(1),
  },
}));

export default CreateClientStepper;
